/**
 * OideSyncPanel
 * Coláiste Lurgan Management System - Oide integration sync controls
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';

import { useToast } from '../../hooks/use-toast';
import { RefreshCw, CheckCircle, AlertCircle, Clock, MailX, Loader2 } from 'lucide-react';

export function OideSyncPanel() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: stats, isLoading, refetch } = useQuery({
    queryKey: ['oide-sync-stats'],
    queryFn: api.students.oideSyncStats,
    staleTime: 30 * 1000,
  });

  const bulkSyncMutation = useMutation({
    mutationFn: api.students.syncOideBulk,
    onSuccess: (data) => {
      toast({
        title: t('settings.oide.bulkQueued', 'Sync Queued'),
        description: t('settings.oide.bulkQueuedMessage', '{{count}} students queued for Oide sync.', { count: data.queued }),
        variant: 'success',
      });
      setTimeout(() => refetch(), 2000);
    },
    onError: () => {
      toast({
        title: t('common.error', 'Error'),
        description: t('settings.oide.bulkError', 'Failed to queue Oide sync. Please try again.'),
        variant: 'destructive',
      });
    },
  });

  const statCards = [
    {
      label: t('settings.oide.stats.synced', 'Synced'),
      value: stats?.synced ?? '—',
      icon: <CheckCircle className="h-5 w-5 text-green-500" />,
      badge: 'default',
    },
    {
      label: t('settings.oide.stats.pending', 'Awaiting Sync'),
      value: stats?.pending ?? '—',
      icon: <Clock className="h-5 w-5 text-amber-500" />,
      badge: 'secondary',
    },
    {
      label: t('settings.oide.stats.failed', 'Failed'),
      value: stats?.failed ?? '—',
      icon: <AlertCircle className="h-5 w-5 text-destructive" />,
      badge: 'destructive',
    },
    {
      label: t('settings.oide.stats.noEmail', 'No Email'),
      value: stats?.no_email ?? '—',
      icon: <MailX className="h-5 w-5 text-muted-foreground" />,
      badge: 'outline',
    },
  ];

  const unsyncedCount = (stats?.pending ?? 0) + (stats?.failed ?? 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.oide.title', 'Oide Integration')}</CardTitle>
        <CardDescription>
          {t('settings.oide.description', 'Sync student accounts to the Oide external learning platform. Students are synced automatically when their email address is first added.')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">

        {/* Stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {statCards.map((card) => (
            <div key={card.label} className="flex flex-col items-center gap-2 p-4 rounded-lg border bg-card text-center">
              {card.icon}
              <span className="text-2xl font-bold">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : card.value}
              </span>
              <span className="text-xs text-muted-foreground">{card.label}</span>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={() => bulkSyncMutation.mutate()}
            disabled={bulkSyncMutation.isPending || unsyncedCount === 0}
          >
            {bulkSyncMutation.isPending
              ? <Loader2 className="h-4 w-4 animate-spin mr-2" />
              : <RefreshCw className="h-4 w-4 mr-2" />
            }
            {t('settings.oide.syncAll', 'Sync All Unsynced ({{count}})', { count: unsyncedCount })}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            {t('common.refresh', 'Refresh')}
          </Button>
        </div>

        {stats?.failed > 0 && (
          <p className="text-sm text-destructive">
            {t('settings.oide.failedHint', '{{count}} student(s) failed to sync — check their records for the Action Required banner.', { count: stats.failed })}
          </p>
        )}

      </CardContent>
    </Card>
  );
}

export default OideSyncPanel;
